import settlementService from '@/services/settlementService';

const state = {
    settlements: [],
    currentSettlement: null,
    loading: {
        settlements: false,
        settlement: false,
        create: false,
        calculate: false,
        approve: false,
        markPaid: false,
    },
    error: null,
};

const mutations = {
    SET_SETTLEMENTS(state, settlements) {
        state.settlements = settlements;
    },
    SET_CURRENT_SETTLEMENT(state, settlement) {
        state.currentSettlement = settlement;
    },
    ADD_SETTLEMENT(state, settlement) {
        state.settlements.push(settlement);
    },
    UPDATE_SETTLEMENT(state, updatedSettlement) {
        const index = state.settlements.findIndex((s) => s.uuid === updatedSettlement.uuid);
        if (index !== -1) {
            state.settlements.splice(index, 1, updatedSettlement);
        }
        if (state.currentSettlement?.uuid === updatedSettlement.uuid) {
            state.currentSettlement = updatedSettlement;
        }
    },
    REMOVE_SETTLEMENT(state, settlementUuid) {
        state.settlements = state.settlements.filter((s) => s.uuid !== settlementUuid);
        if (state.currentSettlement?.uuid === settlementUuid) {
            state.currentSettlement = null;
        }
    },
    SET_LOADING(state, { key, value }) {
        state.loading[key] = value;
    },
    SET_ERROR(state, error) {
        state.error = error;
    },
    CLEAR_CURRENT_SETTLEMENT(state) {
        state.currentSettlement = null;
    },
};

const actions = {
    async fetchSettlements({ commit }, params = {}) {
        commit('SET_LOADING', { key: 'settlements', value: true });
        commit('SET_ERROR', null);
        try {
            const settlements = await settlementService.getSettlements(params);
            commit('SET_SETTLEMENTS', settlements || []);
            return settlements;
        } catch (error) {
            commit('SET_ERROR', error);
            console.error('Failed to fetch settlements:', error);
            throw error;
        } finally {
            commit('SET_LOADING', { key: 'settlements', value: false });
        }
    },

    async fetchSettlement({ commit }, uuid) {
        commit('SET_LOADING', { key: 'settlement', value: true });
        commit('SET_ERROR', null);
        try {
            const settlement = await settlementService.getSettlement(uuid);
            commit('SET_CURRENT_SETTLEMENT', settlement);
            return settlement;
        } catch (error) {
            commit('SET_ERROR', error);
            console.error('Failed to fetch settlement:', error);
            throw error;
        } finally {
            commit('SET_LOADING', { key: 'settlement', value: false });
        }
    },

    async createSettlement({ commit }, settlementData) {
        commit('SET_LOADING', { key: 'create', value: true });
        commit('SET_ERROR', null);
        try {
            const settlement = await settlementService.createSettlement(settlementData);
            commit('ADD_SETTLEMENT', settlement);
            commit('SET_CURRENT_SETTLEMENT', settlement);
            return settlement;
        } catch (error) {
            commit('SET_ERROR', error);
            console.error('Failed to create settlement:', error);
            throw error;
        } finally {
            commit('SET_LOADING', { key: 'create', value: false });
        }
    },

    async calculateSettlement({ commit, state }, uuid) {
        commit('SET_LOADING', { key: 'calculate', value: true });
        commit('SET_ERROR', null);
        try {
            await settlementService.calculateSettlement(uuid);

            const updatedSettlement = await settlementService.getSettlement(uuid);
            commit('UPDATE_SETTLEMENT', updatedSettlement);
            if (state.currentSettlement?.uuid === uuid) {
                commit('SET_CURRENT_SETTLEMENT', updatedSettlement);
            }
            return updatedSettlement;
        } catch (error) {
            commit('SET_ERROR', error);
            console.error('Failed to calculate settlement:', error);
            throw error;
        } finally {
            commit('SET_LOADING', { key: 'calculate', value: false });
        }
    },

    async approveSettlement({ commit, state }, uuid) {
        commit('SET_LOADING', { key: 'approve', value: true });
        commit('SET_ERROR', null);
        try {
            await settlementService.approveSettlement(uuid);

            const updatedSettlement = await settlementService.getSettlement(uuid);
            commit('UPDATE_SETTLEMENT', updatedSettlement);
            if (state.currentSettlement?.uuid === uuid) {
                commit('SET_CURRENT_SETTLEMENT', updatedSettlement);
            }
            return updatedSettlement;
        } catch (error) {
            commit('SET_ERROR', error);
            console.error('Failed to approve settlement:', error);
            throw error;
        } finally {
            commit('SET_LOADING', { key: 'approve', value: false });
        }
    },

    async markSettlementPaid({ commit, state }, uuid) {
        commit('SET_LOADING', { key: 'markPaid', value: true });
        commit('SET_ERROR', null);
        try {
            await settlementService.markSettlementPaid(uuid);

            const updatedSettlement = await settlementService.getSettlement(uuid);
            commit('UPDATE_SETTLEMENT', updatedSettlement);
            if (state.currentSettlement?.uuid === uuid) {
                commit('SET_CURRENT_SETTLEMENT', updatedSettlement);
            }
            return updatedSettlement;
        } catch (error) {
            commit('SET_ERROR', error);
            console.error('Failed to mark settlement as paid:', error);
            throw error;
        } finally {
            commit('SET_LOADING', { key: 'markPaid', value: false });
        }
    },

    clearCurrentSettlement({ commit }) {
        commit('CLEAR_CURRENT_SETTLEMENT');
    },
};

const getters = {
    settlements: (state) => state.settlements,
    currentSettlement: (state) => state.currentSettlement,
    getSettlementByUuid: (state) => (uuid) => state.settlements.find((s) => s.uuid === uuid),
    settlementsByDriver: (state) => (driverNumber) =>
        state.settlements.filter((s) => s.driver?.driverNumber === driverNumber),
    settlementsByStatus: (state) => (status) =>
        state.settlements.filter((s) => s.status === status),
    draftSettlements: (state) => state.settlements.filter((s) => s.status === 'draft'),
    approvedSettlements: (state) => state.settlements.filter((s) => s.status === 'approved'),
    paidSettlements: (state) => state.settlements.filter((s) => s.status === 'paid'),
    isLoading: (state) => (key) => state.loading[key] || false,
    isLoadingAny: (state) => Object.values(state.loading).some((loading) => loading),
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
